import React, {useEffect, useMemo, useState} from "react";
import {Form, Grid, Typography} from "antd";
import type {ColumnsType} from "antd/es/table";
import type {FormInstance} from "antd/es/form";
import {EventCategoryStatistic, EventDrinkStatistic, EventStatistics, EventSummary, EventTopConsumer} from "../../services/drinkService";
import {AuthUser, useAuth} from "../../context/AuthContext";
import {deleteEvent, updateEventAccess} from "../../services/drinks/EventService";
import {formatAlcohol, formatNumber, formatVolume} from "./eventStats/utils";
import {EventVisibility} from "../../generated/drinks_pb";
import {buildEventVisibilityOptions} from "./eventVisibilityOptions";
import {useTranslation} from "react-i18next";
import type {TFunction} from "i18next";
import TopConsumerCell from "./eventStats/TopConsumerCell";
import {buildUserColumns} from "./eventStats/userColumns";
import {useEventStatsLoading} from "./useEventStatsLoading";
import {useEventStatsUiState} from "./useEventStatsUiState";
import {useEventStatsDerived} from "./useEventStatsDerived";
import {useAppFeedback} from "../useAppFeedback";
const {Text} = Typography;

const saveSelectedEventAccess = async (
    user: AuthUser | null,
    event: EventSummary | null,
    accessForm: FormInstance<{ visibility: EventVisibility; password?: string }>,
    setUpdatingAccess: React.Dispatch<React.SetStateAction<boolean>>,
    setEvents: React.Dispatch<React.SetStateAction<EventSummary[]>>,
    t: TFunction,
    message: ReturnType<typeof useAppFeedback>["message"]
) => {
    if (!user || !event) return;
    try {
        const values = await accessForm.validateFields();
        setUpdatingAccess(true);
        const password = values.visibility === EventVisibility.EVENT_VISIBILITY_PASSWORD ? values.password ?? "" : null;
        const updatedEvent = await updateEventAccess(user, event.eventId, { visibility: values.visibility, password });
        setEvents((previous) => previous.map((item) => item.eventId === updatedEvent.eventId ? updatedEvent : item));
        message.success(t("eventStats.messages.visibilityUpdated"));
    } catch (error: any) {
        if (error?.errorFields) return;
        message.error(t("eventStats.messages.visibilityUpdateError"));
    } finally {
        setUpdatingAccess(false);
    }
};

const deleteSelectedEvent = async (
    user: AuthUser | null,
    event: EventSummary | null,
    setDeleting: React.Dispatch<React.SetStateAction<boolean>>,
    setSelectedEventId: React.Dispatch<React.SetStateAction<number | null>>,
    setStats: React.Dispatch<React.SetStateAction<EventStatistics | null>>,
    loadEvents: () => Promise<void>,
    t: TFunction,
    message: ReturnType<typeof useAppFeedback>["message"]
) => {
    if (!user || !event) return;
    setDeleting(true);
    try {
        await deleteEvent(user, event.eventId);
        message.success(t("eventStats.messages.deleteSuccess"));
        setSelectedEventId(null);
        setStats(null);
        await loadEvents();
    } catch {
        message.error(t("eventStats.messages.deleteError"));
    } finally {
        setDeleting(false);
    }
};

export const useEventStats = () => {

    const { message } = useAppFeedback();
    const { isAuthenticated, user } = useAuth();
    const { t } = useTranslation();

    const data = useEventStatsLoading(isAuthenticated, user, t);
    const { events, setEvents, selectedEventId, setSelectedEventId, stats, setStats,
        loadingEvents, loadingStats, error, setError, ongoingLeaderboards, loadingOngoing,
        loadEvents, loadOngoing, handleEventSelect } = data;
    const { selectedDrinkId, setSelectedDrinkId, visibleUserIds, setVisibleUserIds,
        visibleUserSet, allParticipantIds, showAllParticipants } = useEventStatsUiState(stats);
    const [accessForm] = Form.useForm<{ visibility: EventVisibility; password?: string }>();
    const [updatingAccess, setUpdatingAccess] = useState<boolean>(false);
    const [deletingSelectedEvent, setDeletingSelectedEvent] = useState<boolean>(false);
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const mobileTableScroll = useMemo(() => (isMobile ? { x: "max-content" as const } : undefined), [isMobile]);
    const { options: visibilityOptions, optionMap: visibilityOptionMap } = useMemo(() => buildEventVisibilityOptions(t), [t]);
    const selectedAccessVisibility = Form.useWatch("visibility", accessForm) as EventVisibility | undefined;
    const selectedEvent = useMemo(() => events.find((evt) => evt.eventId === selectedEventId) ?? null, [events, selectedEventId]);
    const selectedAccessEventId = selectedEvent?.eventId;
    const selectedAccessEventVisibility = selectedEvent?.visibility;

    const derived = useEventStatsDerived({ stats, selectedDrinkId, visibleUserSet, showAllParticipants });
    const { visibleDrinks, visibleCategories, selectedDrinkStats, filteredUsers, filteredTotals,
        topAlcoholUser, timelineSeries, topBeerEntry, topShotsEntry, selectedDrinkLeaderboard,
        drinkOptions, participantOptions } = derived;

    useEffect(() => {
        if (selectedAccessEventId === undefined) {
            accessForm.resetFields();
            return;
        }

        accessForm.setFieldsValue({
            visibility: selectedAccessEventVisibility!,
            password: "",
        });
    }, [accessForm, selectedAccessEventId, selectedAccessEventVisibility]);

    const handleSelectedEventAccessUpdate = () =>
        saveSelectedEventAccess(user, selectedEvent, accessForm, setUpdatingAccess, setEvents, t, message);

    const handleDeleteSelectedEvent = () =>
        deleteSelectedEvent(user, selectedEvent, setDeletingSelectedEvent, setSelectedEventId, setStats, loadEvents, t, message);

    const userColumns = useMemo(
        () => buildUserColumns(t, isMobile, selectedDrinkLeaderboard),
        [isMobile, selectedDrinkLeaderboard, t]
    );
    const drinkColumns: ColumnsType<EventDrinkStatistic> = useMemo(
        () => [
            {
                title: t("eventStats.tables.drinkColumns.drink"),
                dataIndex: "drinkName",
                key: "drinkName",
                render: (value: string) => <Text strong>{value}</Text>,
            },
            {
                title: t("eventStats.tables.drinkColumns.entries"),
                dataIndex: "activityCount",
                render: (value: number) => formatNumber(value),
                key: "drinkActivityCount",
                align: "right",
            },
            {
                title: t("eventStats.tables.drinkColumns.liquid"),
                dataIndex: "totalVolumeMl",
                key: "drinkTotalVolume",
                align: "right",
                render: (value: number) => formatVolume(value),
            },
            {
                title: t("eventStats.tables.drinkColumns.alcohol"),
                dataIndex: "totalAlcoholGrams",
                key: "drinkTotalAlcohol",
                align: "right",
                render: (value: number) => formatAlcohol(value),
            },
            {
                title: t("eventStats.tables.drinkColumns.topConsumer"),
                dataIndex: "topConsumer",
                key: "topConsumer",
                render: (consumer: EventTopConsumer | null) => <TopConsumerCell consumer={consumer} />,
            },
        ],
        [t]
    );

    const categoryColumns: ColumnsType<EventCategoryStatistic> = useMemo(
        () => [
            {
                title: t("eventStats.tables.categoryColumns.category"),
                dataIndex: "category",
                key: "category",
                render: (value: string) => <Text strong>{value}</Text>,
            },
            {
                title: t("eventStats.tables.categoryColumns.entries"),
                dataIndex: "activityCount",
                render: (value: number) => formatNumber(value),
                key: "categoryActivityCount",
                align: "right",
            },
            {
                title: t("eventStats.tables.categoryColumns.liquid"),
                dataIndex: "totalVolumeMl",
                key: "categoryVolume",
                align: "right",
                render: (value: number) => formatVolume(value),
            },
            {
                title: t("eventStats.tables.categoryColumns.alcohol"),
                dataIndex: "totalAlcoholGrams",
                key: "categoryAlcohol",
                align: "right",
                render: (value: number) => formatAlcohol(value),
            },
            {
                title: t("eventStats.tables.categoryColumns.topConsumer"),
                dataIndex: "topConsumer",
                key: "categoryTopConsumer",
                render: (consumer: EventTopConsumer | null) => <TopConsumerCell consumer={consumer} />,
            },
        ],
        [t]
    );


    const canManageSelectedEvent = Boolean(selectedEvent && user && selectedEvent.ownerUserId === user.userId && !selectedEvent.isActive);
    const requiresAccessPassword = selectedAccessVisibility === EventVisibility.EVENT_VISIBILITY_PASSWORD;
    return {isAuthenticated, user, t, events, selectedEventId, stats, loadingEvents, loadingStats, error,
        selectedDrinkId, visibleUserIds, allParticipantIds, showAllParticipants, ongoingLeaderboards, loadingOngoing,
        accessForm, updatingAccess, deletingSelectedEvent, isMobile, mobileTableScroll, visibilityOptions,
        visibilityOptionMap, selectedEvent, handleEventSelect, loadEvents, loadOngoing, setError, setSelectedDrinkId, setVisibleUserIds,
        handleSelectedEventAccessUpdate, handleDeleteSelectedEvent, visibleDrinks, visibleCategories,
        selectedDrinkStats, filteredUsers, filteredTotals, topAlcoholUser, timelineSeries, topBeerEntry,
        topShotsEntry, selectedDrinkLeaderboard, drinkOptions, participantOptions, userColumns, drinkColumns,
        categoryColumns, canManageSelectedEvent, requiresAccessPassword};

};

export type EventStatsData = ReturnType<typeof useEventStats>;
